import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Search, ClipboardList, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABELS = {
  coding_interest: 'Coding',
  robotics_interest: 'Robotics',
  science_interest: 'Science',
  engineering_interest: 'Engineering',
  math_interest: 'Math',
  notify_me_camp: 'Notify Me',
  volunteer: 'Volunteer',
  generic: 'Generic',
  summer_camps_interest: 'Summer Camps',
};

const SOURCE_COLORS = {
  coding_interest: 'bg-blue-100 text-blue-700',
  robotics_interest: 'bg-purple-100 text-purple-700',
  science_interest: 'bg-green-100 text-green-700',
  engineering_interest: 'bg-orange-100 text-orange-700',
  math_interest: 'bg-yellow-100 text-yellow-700',
  notify_me_camp: 'bg-pink-100 text-pink-700',
  volunteer: 'bg-teal-100 text-teal-700',
  summer_camps_interest: 'bg-amber-100 text-amber-700',
  generic: 'bg-slate-100 text-slate-700',
};

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function AdminInterestFormsPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unhandled');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contact-submissions/?limit=500');
      setRows(data.submissions || []);
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== 'bots') list = list.filter(r => !r.dropped_reason);
    if (filter === 'unhandled') list = list.filter(r => !r.handled);
    if (filter === 'handled') list = list.filter(r => r.handled);
    if (filter === 'bots') list = list.filter(r => !!r.dropped_reason);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(needle)
        || (r.email || '').toLowerCase().includes(needle)
        || (r.message || '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [rows, filter, q]);

  const counts = useMemo(() => ({
    all: rows.filter(r => !r.dropped_reason).length,
    unhandled: rows.filter(r => !r.handled && !r.dropped_reason).length,
    handled: rows.filter(r => r.handled).length,
    bots: rows.filter(r => !!r.dropped_reason).length,
  }), [rows]);

  const markHandled = async (row, handled) => {
    try {
      await api.post(`/admin/contact-submissions/${row.id}/`, { handled });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, handled } : r));
      toast.success(handled ? 'Marked as handled' : 'Marked as unhandled');
    } catch {
      toast.error('Update failed');
    }
  };

  const openEdit = (row) => { setEditing(row); setEditNotes(row.admin_notes || ''); };

  const saveNotes = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.post(`/admin/contact-submissions/${editing.id}/`, { admin_notes: editNotes });
      setRows(prev => prev.map(r => r.id === editing.id ? { ...r, admin_notes: editNotes } : r));
      toast.success('Notes saved');
      setEditing(null);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const purgeBots = async () => {
    if (!window.confirm(`Hard-delete all ${counts.bots} bot-dropped submissions? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post('/admin/contact-submissions/purge-bots/', {});
      toast.success(`Deleted ${data.contact_deleted || 0} bot submissions`);
      load();
    } catch {
      toast.error('Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" /> Interest Form Submissions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Division interest forms, notify-me requests, volunteer inquiries, and summer camps interest.
          </p>
        </div>
        {counts.bots > 0 && (
          <Button variant="destructive" size="sm" onClick={purgeBots} disabled={purging}>
            {purging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Purge {counts.bots} Bot Submissions
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="unhandled">
              New <Badge className="ml-1.5 bg-amber-500/10 text-amber-700">{counts.unhandled}</Badge>
            </TabsTrigger>
            <TabsTrigger value="handled">
              Handled <Badge className="ml-1.5">{counts.handled}</Badge>
            </TabsTrigger>
            <TabsTrigger value="bots">
              Bot Dropped <Badge className="ml-1.5 bg-red-100 text-red-700">{counts.bots}</Badge>
            </TabsTrigger>
            <TabsTrigger value="all">
              All Real <Badge className="ml-1.5">{counts.all}</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name or email…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-8 w-56"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No submissions match this filter.</p>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 font-medium">Name</th>
                <th className="text-left px-4 py-2.5 font-medium">Email / Phone</th>
                <th className="text-left px-4 py-2.5 font-medium">Source</th>
                <th className="text-left px-4 py-2.5 font-medium">Message</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(row => (
                <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {fmt(row.created_at)}
                  </td>
                  <td className="px-4 py-3 font-medium">{row.name || '—'}</td>
                  <td className="px-4 py-3">
                    <div>{row.email || '—'}</div>
                    {row.phone && <div className="text-muted-foreground text-xs">{row.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SOURCE_COLORS[row.source] || SOURCE_COLORS.generic}`}>
                      {SOURCE_LABELS[row.source] || row.source}
                    </span>
                    {row.dropped_reason && (
                      <div className="mt-1">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                          {row.dropped_reason}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="line-clamp-2 text-sm">{row.message || row.subject || '—'}</p>
                    {row.admin_notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic line-clamp-1">
                        Note: {row.admin_notes}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.dropped_reason ? (
                      <Badge variant="outline" className="text-red-600 border-red-200">Dropped</Badge>
                    ) : row.handled ? (
                      <Badge variant="outline" className="text-green-600 border-green-200">
                        <Check className="h-3 w-3 mr-1" /> Handled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-200">New</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                        Notes
                      </Button>
                      {!row.dropped_reason && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markHandled(row, !row.handled)}
                          className={row.handled ? 'text-amber-600' : 'text-green-600'}
                        >
                          {row.handled ? 'Unhandle' : 'Handle'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin Notes — {editing?.name || editing?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-muted-foreground space-y-1">
              <div><strong>Email:</strong> {editing?.email}</div>
              {editing?.phone && <div><strong>Phone:</strong> {editing.phone}</div>}
              <div><strong>Source:</strong> {SOURCE_LABELS[editing?.source] || editing?.source}</div>
              <div><strong>Message:</strong> {editing?.message || editing?.subject}</div>
            </div>
            <Textarea
              placeholder="Internal notes…"
              value={editNotes}
              onChange={e => setEditNotes(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveNotes} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
